// StopServiceLineDialog — the one thing that stops a service line.
//
// It replaces a browser pop-up that said "End this service line?" and recorded the
// word and nothing else. That door is where 77 stopped lines with no last night
// came from, and it is why nobody could answer "when did we stop cleaning that
// building".
//
// The rules here are the ones already ruled for the sub change, not new ones:
//
//   * ⛔ THE LAST NIGHT IS ASKED, NEVER COMPUTED. Nothing records which nights a
//     building is cleaned and some are cleaned seven nights a week, so any
//     calculation would be a guess dressed as a fact (TCSD-0194).
//   * ⛔ "I DON'T KNOW YET" IS A REAL ANSWER. It blocks nothing. The line still
//     stops, the sub still stops being paid, and the MISSING date becomes
//     Finance's flag — there is no second field and no second status.
//   * ⛔ A PAST DATE IS NEVER REFUSED AND NEVER WARNED AS LATE. Most changes are
//     recorded after the fact. The system exists to make the record right before
//     pay runs, not to police when the account manager typed it (TCSD-0188).
//   * A FUTURE DATE books it: the line keeps running and stops on its own night.
//
// Nothing is deleted, ever. A line that has been invoiced stays readable.
// ============================================================================

import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { errorMessage } from '../../shared/errors';
import { Field, INPUT_CLS } from './intake-parts';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function StopServiceLineDialog({
  open,
  assignmentId,
  label,
  startedOn,
  onClose,
  onStopped,
}: {
  open: boolean;
  assignmentId: string;
  /** What the reader is stopping, in their words. */
  label: string;
  /** The line's first night, so the dialog can say why a date is refused. */
  startedOn?: string | null;
  onClose: () => void;
  onStopped: (result: { known: boolean; scheduled: boolean; lastNight: string | null }) => void;
}) {
  const [known, setKnown] = useState(true);
  const [lastNight, setLastNight] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) return null;

  const isFuture = known && lastNight > todayIso();
  const isPast = known && lastNight < todayIso();

  async function submit() {
    setSaving(true);
    setErr(null);
    try {
      const { data, error } = await supabase.rpc('stop_service_line', {
        p_assignment_id: assignmentId,
        p_last_night: known ? lastNight || null : null,
        p_reason: reason.trim() || null,
      });
      if (error) throw error;
      const r = (data ?? {}) as { known?: boolean; scheduled?: boolean; last_night?: string | null };
      onStopped({
        known: r.known ?? known,
        scheduled: r.scheduled ?? false,
        lastNight: r.last_night ?? null,
      });
      onClose();
    } catch (e) {
      setErr(errorMessage(e, 'Could not stop the line.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-tcs-border bg-tcs-overlay p-5 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-tcs-text">Stop {label}</h3>
            <p className="mt-1 text-xs text-tcs-text-muted">
              Nothing is deleted. The line stays readable with everything it was paid.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 grid gap-3">
          <Field label="When was the last night?">
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setKnown(true)}
                className={`rounded-md border px-3 py-1.5 text-sm ${known ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink' : 'border-tcs-border bg-tcs-raised text-tcs-text hover:bg-tcs-bg-muted'}`}
              >
                I know the date
              </button>
              <button
                type="button"
                onClick={() => setKnown(false)}
                className={`rounded-md border px-3 py-1.5 text-sm ${!known ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink' : 'border-tcs-border bg-tcs-raised text-tcs-text hover:bg-tcs-bg-muted'}`}
              >
                I don&apos;t know yet
              </button>
            </div>
          </Field>

          {known ? (
            <Field label="Last night worked">
              <input
                type="date"
                value={lastNight}
                min={startedOn ?? undefined}
                onChange={(e) => setLastNight(e.target.value)}
                className={INPUT_CLS}
              />
              {/* One field, no mode. The line underneath just says what that date
                  means — it never scolds anyone for a past date. */}
              <p className="mt-1 text-xs text-tcs-text-secondary">
                {isFuture
                  ? 'Booked. The line keeps running and stops on its own that night.'
                  : isPast
                    ? 'Already happened. Recorded as of that night.'
                    : 'Stops tonight.'}
              </p>
            </Field>
          ) : (
            <p className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-secondary">
              The line stops now and the sub stops being paid. The missing date is
              Finance&apos;s flag — fill it in on the line when you know it.
            </p>
          )}

          <Field label="Why (optional)">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={INPUT_CLS}
              placeholder="Client cancelled this service, work moved to another crew…"
            />
          </Field>

          {err && <p className="text-xs text-rag-red">{err}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md border border-tcs-inverse bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isFuture ? 'Book the stop' : 'Stop the line'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
